import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { Switch } from "@mui/material";
import "./AddItem.css";
import { FaTrashAlt } from "react-icons/fa";
import { message } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import APIURL, { IMAGE_BASE_URL } from "../api/apiClient";
import Loader from "../components/Loader";

const EditScreen = () => {
  const [loading, setLoading] = useState(false);
  const { id } = useParams(); // Ensure `id` is retrieved correctly from the URL
  const [values, setValues] = useState({
    name: '',
    brand: '',
    image: '',
    thumbnailPreview: '',
    images: [],
    imagePreviews: [],
    description: '',
    category_id: '',
    category_ids: [],
    actual_price: '',
    selling_price: '',
    tax: '',
    discount: '',
    status: '',
    stock: '',
    maximum_cart_quantity: '',
    is_approved: '',
    refurbished: '',
    net_weight: '',
dimensions: '',
  });


  const [categoryids, setCategoryids] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const fileImageRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchItem = async () => {
      setLoading(true);
      try {
        const response = await APIURL.get(`/fetchitemid.php?id=${id}`);
        const baseImageUrl = IMAGE_BASE_URL;

        let imagesArray = [];
        if (response.images) {
          try {
            imagesArray = JSON.parse(response.images);
          } catch (err) {
            console.error("Error parsing images:", err);
          }
        }

        const categoryIds = response.category_ids ? JSON.parse(response.category_ids) : [];

        setValues(prev => ({
          ...prev,
          ...response,
          image: response.image ? `${baseImageUrl}${response.image}` : null,
          images: imagesArray.map(img => `${baseImageUrl}${img}`),
          category_ids: categoryIds,
          category_id: response.category_id, // <- set main category
        }));

        // ✅ Set selectedCategory after categories are loaded
        if (categoryids.length > 0 && response.category_id) {
          const mainCategoryOption = categoryids.find(c => c.value === parseInt(response.category_id));
          if (mainCategoryOption) setSelectedCategory(mainCategoryOption);
        }

      } catch (err) {
        console.error("Error fetching item:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [id, categoryids]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const newDataRaw = await APIURL.get("/fetchcategories.php");

        // Make sure it's an array
        const newData = Array.isArray(newDataRaw) ? newDataRaw : JSON.parse(newDataRaw);

        const formattedCategories = newData.map((category) => ({
          value: category.id,
          label: category.name,
        }));

        setCategoryids(formattedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedCategory) {
      setSubCategories([]);
      setSelectedSubCategory(null);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const res = await APIURL.get(`/subCategories.php?category_id=${selectedCategory.value}`);

        // API returns { status: true, data: [...] }
        if (res.status && Array.isArray(res.data)) {
          const formattedSubCats = res.data.map(sub => ({
            value: sub.id,
            label: sub.name
          }));

          setSubCategories(formattedSubCats.length > 0 ? formattedSubCats : []);
          setSelectedSubCategory(null);
        } else {
          setSubCategories([]);
          setSelectedSubCategory(null);
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        setSubCategories([]);
        setSelectedSubCategory(null);
      }
    };

    fetchSubCategories();
  }, [selectedCategory]);


  // Main category change
  const handleMainCategoryChange = (option) => {
    setSelectedCategory(option); // for subcategory useEffect
    setSelectedSubCategory(null); // reset subcategory
    setValues(prev => ({
      ...prev,
      category_id: option ? option.value : '', // sync with Select value
      category_ids: [], // reset subcategory ids
    }));
  };

  // Subcategory change
  const handleSubCategoryChange = (option) => {
    setSelectedSubCategory(option);
    setValues(prev => ({
      ...prev,
      category_ids: option ? [option.value] : [],
    }));
  };


  const calculateDiscount = (actual, selling) => {
    if (!actual || actual <= 0 || !selling) return '';
    const discount = ((actual - selling) / actual) * 100;
    return discount.toFixed(2);
  };

  const calculateSellingPrice = (actual, discount) => {
    if (!actual || actual <= 0 || !discount) return '';
    const selling = actual - (actual * (discount / 100));
    return selling.toFixed(2);
  };

  const handleChange = (e) => {
    const { name, type, checked, files, value } = e.target;

    if (type === "checkbox") {
      setValues(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
      return;
    }

    if (type === "file") {
      if (name === "image") {
        const file = files[0];
        const preview = URL.createObjectURL(file);
        setValues(prev => ({ ...prev, image: file, thumbnailPreview: preview }));
      }
      else if (name === "images") {
        const imageFiles = Array.from(files);
        const existingImages = values.images || [];
        const existingFileObjects = values.imageFiles || [];
        const totalImages = [...existingImages, ...existingFileObjects, ...imageFiles].slice(0, 5);
        const newPreviews = totalImages
          .filter(img => img instanceof File)
          .map(file => URL.createObjectURL(file));

        setValues(prev => ({
          ...prev,
          images: totalImages.filter(img => typeof img === "string"),
          imageFiles: totalImages.filter(img => img instanceof File),
          imagePreviews: newPreviews,
        }));

        if (fileImageRef.current) fileImageRef.current.value = "";
      }
      return;
    }

    // Normal text/number fields
    setValues(prev => {
      let updatedValues = { ...prev, [name]: value };


      // // ✅ Ensure category_ids stays an array
      // if (name === "category_ids") {
      //   try {
      //     updatedValues = JSON.parse(value);
      //   } catch (error) {
      //     updatedValues = value; // Keep as-is if not JSON
      //   }
      // }
      // Discount calculation logic
      if (name === "actual_price" || name === "selling_price") {
        const actual = parseFloat(updatedValues.actual_price);
        const selling = parseFloat(updatedValues.selling_price);
        updatedValues.discount = Math.round(calculateDiscount(actual, selling)); // Round instantly
      }

      if (name === "discount") {
        const n = parseFloat(updatedValues.discount);
        updatedValues.discount = Number.isFinite(n) ? Math.round(n) : '';
      }

      if (name === "actual_price" || name === "discount") {
        const actual = parseFloat(updatedValues.actual_price);
        const discount = parseFloat(updatedValues.discount);
        updatedValues.selling_price = calculateSellingPrice(actual, discount);
      }

      return updatedValues;
    });
  };


  const handleDescriptionChange = (content) => {
    setValues(prev => ({
      ...prev,
      description: content,
    }));
  };


  const handleDeleteImage = async (e, index) => {
    e.preventDefault();

    let imagePath;
    let isFetchedImage;

    // Get current state without updating it
    const currentValues = values;
    isFetchedImage = index < (currentValues.images || []).length;

    if (isFetchedImage) {
      imagePath = currentValues.images[index];
    } else {
      const previewIndex = index - (currentValues.images?.length || 0);
      imagePath = currentValues.imagePreviews[previewIndex];
    }

    // Extract just the filename (works for full URLs too)
    const fileName = imagePath.split("/").pop();

    try {
      const res = await axios.post(
        APIURL.getFullUrl("deleteImage.php"),
        { imagePath: fileName }
      );
      // console.log(res.data);

      // Remove from UI after successful deletion
      setValues((prevValues) => {
        if (isFetchedImage) {
          return {
            ...prevValues,
            images: prevValues.images.filter((_, i) => i !== index),
          };
        } else {
          const previewIndex = index - (prevValues.images?.length || 0);
          return {
            ...prevValues,
            imagePreviews: prevValues.imagePreviews.filter((_, i) => i !== previewIndex),
          };
        }
      });
    } catch (err) {
      console.error("Error deleting image:", err);
    }
  };

let parsedCategoryIds;

try {
  const val = values.category_ids;

  if (Array.isArray(val)) {
    // already an array
    parsedCategoryIds = val;
  } else if (typeof val === "string") {
    // clean and detect JSON or CSV
    const trimmed = val.trim();

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      // JSON array format: "[1,2,3]"
      parsedCategoryIds = JSON.parse(trimmed);
    } else if (trimmed.includes(",")) {
      // CSV format: "1,2,3"
      parsedCategoryIds = trimmed
        .split(",")
        .map(v => v.trim())
        .filter(Boolean)
        .map(Number)
        .filter(n => !isNaN(n));
    } else if (trimmed !== "") {
      // single value like "5"
      const num = Number(trimmed);
      parsedCategoryIds = !isNaN(num) ? [num] : [trimmed];
    } else {
      parsedCategoryIds = [];
    }
  } else if (typeof val === "number") {
    // single numeric value
    parsedCategoryIds = [val];
  } else {
    parsedCategoryIds = [];
  }
} catch (err) {
  parsedCategoryIds = [];
}

// ✅ Always ends up as an array
console.log("Parsed:", parsedCategoryIds);


  const handleSubmit = (e) => {
    e.preventDefault();

    if (!id) {
      console.error("ID is missing. Cannot submit the form.");
      return;
    }

    const formData = new FormData();
    formData.append("id", id);

    // ✅ Base URL to remove from existing image paths
    const BASE_URL = IMAGE_BASE_URL;

    // ✅ Append category_ids correctly
    if (values.category_ids) {
      const cleanCategoryIds = Array.isArray(values.category_ids)
        ? values.category_ids
        : JSON.parse(values.category_ids || "[]");
      formData.append("category_ids", JSON.stringify(values.category_ids))
    }

    // ✅ Append single image file (thumbnail)
    if (values.image) {
      formData.append("image", values.image);
    }

    // ✅ Append existing image URLs (Convert to relative paths)
    if (values.images && values.images.length > 0) {
      values.images.forEach((imageUrl) => {
        if (typeof imageUrl === "string") {
          const relativePath = imageUrl.replace(BASE_URL, ""); // Remove full path
          formData.append("existing_images[]", relativePath);
        }
      });
    }

    // ✅ Append new image files (selected by user)
    if (values.imageFiles && values.imageFiles.length > 0) {
      values.imageFiles.forEach((file) => {
        formData.append("images[]", file);
      });
    }

    // Append other fields, avoiding redundant appends
    Object.entries(values).forEach(([key, value]) => {
      if (!["image", "images", "imagePreviews", "thumbnailPreview", "category_ids"].includes(key) && value !== null && value !== undefined) {
        // console.log(`Adding ${key}:`, value);
        formData.append(key, value);
      }
    });

    // // Debug: Log FormData contents
    // console.log("FormData content:");
    // for (let [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }

    // Submit data to the backend
    axios
      .post(APIURL.getFullUrl("updateitems.php"), formData)
      .then((response) => {
        if (response.data.success) {
          console.log(response.data.message);
          message.success("Item Updated Successfully");
          navigate("/items");
        } else {
          console.error(response.data.error || "An error occurred while updating the item.");
        }
      })
      .catch((err) => {
        console.error("Error during submission:", err);
      });
  };

          // ✅ Full screen loader while fetching
        if (loading) {
          return <Loader type="full" loading={loading} />;
        }

  return (
    <>
      {/* ✅ Loader must be placed outside the main container */}
      {/* <Loader type="full" loading={loading} /> */}

      <div className="form-container">
        <h2 className="form-title">Edit Item</h2>
        <form onSubmit={handleSubmit} formEncType='multipart/form-data' className='form-group'>
          <div className="form-grid">
            {/* Left Column - Form Fields */}
            <div className="form-fields">
              <div className='name-brand'>
                <label htmlFor="name" className="label">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Enter name"
                  className="form-control input"
                  value={values.name}
                  onChange={handleChange}
                  required
                />

                <label htmlFor="brand" className="label">Brand</label>
                <input
                  type="text"
                  id="brand"
                  name="brand"
                  placeholder="Enter your brand"
                  className="form-control input"
                  value={values.brand}
                  onChange={handleChange}
                  required
                />
              </div>

              <label htmlFor="description" className="label">Description</label>
              <ReactQuill
                theme="snow"
                value={values.description}
                onChange={handleDescriptionChange}
                placeholder="Enter description"
                style={{ marginBottom: "50px" }}
              />

              <label htmlFor='category' className='label'>Main Category</label>
              <Select
                name="category_id"
                value={selectedCategory}
                onChange={handleMainCategoryChange}
                options={categoryids}
                placeholder="Select Main Category"
                required
              />


              <label htmlFor="category_id" className="label">Sub Categories</label>
              <Select
                // isMulti
                value={subCategories.filter(option => parsedCategoryIds.includes(option.value))}
                onChange={handleSubCategoryChange}
                options={subCategories}
                placeholder="Select Main Category First"
              // required
              />



              <label htmlFor="actual_price" className="label">Actual Price</label>
              <input
                type="number"
                id="actual_price"
                name="actual_price"
                placeholder="Enter actual price"
                className="form-control input"
                value={values.actual_price}
                onChange={handleChange}
                required
              />

              <label htmlFor="selling_price" className="label">Selling Price</label>
              <input
                type="number"
                id="selling_price"
                name="selling_price"
                placeholder="Enter selling price"
                className="form-control input"
                value={values.selling_price}
                onChange={handleChange}
                required
              />

              <label htmlFor="tax_percentage" className="label">Tax Percentage (%)</label>
              <input
                type="number"
                id="tax_percentage"
                name="tax"
                placeholder="Enter tax percentage"
                className="form-control input"
                value={values.tax}
                onChange={handleChange}
                required
              />

              <div className="switch-container">
                <span className="switch-label">Status</span>
                <Switch
                  name="status"
                  checked={values.status}
                  onChange={(e) => handleChange({ target: { name: 'status', type: 'checkbox', checked: e.target.checked } })}
                />
              </div>

              <label htmlFor="discount" className="label">Discount (%)</label>
              <input
                type="number"
                id="discount"
                name="discount"
                placeholder="Enter discount"
                className="form-control input"
                value={values.discount}
                onChange={handleChange}
                required
              />
            </div>

            {/* Right Column - Images and Additional Fields */}
            <div className="form-sidebar">
              {/* Image Upload Card */}
              <div className='image-upload-card'>
                <h3 className="section-title">Images</h3>

                <div className='thumbnail-upload'>
                  <label htmlFor="thumbnail" className="label-thumbnail">
                    Add <br />Thumbnail
                    <input
                      type="file"
                      id="thumbnail"
                      name="image"
                      onChange={handleChange}
                    />
                  </label>
                  {values.image && (
                    <div className='thumbnail-preview'>
                      <img
                        src={values.thumbnailPreview || values.image}
                        alt="thumbnail Preview"
                        className='thumbnail-img'
                      />
                    </div>
                  )}
                </div>

                <div className='additional-images'>
                  <div className="images-upload-container">
                    <label className="images-label" htmlFor="images">
                      + Add Images <br /> <span>Up to 5 images</span>
                      <input
                        type="file"
                        name="images"
                        accept="image/*"
                        id='images'
                        onChange={handleChange}
                        ref={fileImageRef}
                        multiple
                      />
                    </label>

                    <div className="images-preview">
                      {[
                        ...(values.images || []), // Fetched images
                        ...(values.imagePreviews || []), // Selected images
                      ].map((image, index) => {
                        const isFetchedImage = typeof image === "string";
                        return (
                          <div key={index} className="image-preview-item">
                            <img
                              src={isFetchedImage ? image : image}
                              alt={`Image ${index + 1}`}
                              className="preview-img"
                            />
                            <button
                              type="button"
                              onClick={(e) => handleDeleteImage(e, index)}
                              className="delete-button"
                            >
                              <FaTrashAlt className="trash-icon" />
                            </button>
                            <span className="image-number">{index + 1}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Fields Card */}
              <div className='additional-fields-card'>
                <h3 className="section-title">Additional Details</h3>

                <label htmlFor="stock" className="label">Stock</label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  placeholder="Enter stock"
                  className="form-control input"
                  value={values.stock}
                  onChange={handleChange}
                />

                <label htmlFor="moq" className="label">Maximum Order Quantity</label>
                <input
                  type="number"
                  id="moq"
                  name="maximum_cart_quantity"
                  placeholder="Enter MOQ"
                  className="form-control input"
                  value={values.maximum_cart_quantity}
                  onChange={handleChange}
                />

                <label htmlFor="approved" className="label">Approved</label>
                <select
                  id="approved"
                  name="is_approved"
                  className="custom-select select"
                  value={values.is_approved}
                  onChange={handleChange}
                >
                  <option value="" disabled>Select Option</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>

                <label htmlFor="refurbished" className="label">Refurbished</label>
                <select
                  id="refurbished"
                  name="refurbished"
                  className="custom-select select"
                  value={values.refurbished}
                  onChange={handleChange}
                >
                  <option value="" disabled>Select Option</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>

                  <label htmlFor="net_weight" className="label">Net Weight</label>
              <input
                type="text"
                id="net_weight"
                name="net_weight"
                placeholder="Enter Net Weight"
                className="form-control input"
                value={values.net_weight}
                onChange={handleChange}
              />

              <label htmlFor="dimensions" className="label">Dimensions </label>
              <input
                type="text"
                id="dimensions"
                name="dimensions"
                placeholder="Enter Dimensions"
                className="form-control input"
                value={values.dimensions}
                onChange={handleChange}
              />
              </div>
            </div>
          </div>

          <button type="submit" name='submit' className="btn btn-primary submit-button">
            Update
          </button>
        </form>
      </div>
    </>
  );


}

export default EditScreen;

